import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {BreadCrumb} from "primereact/breadcrumb";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Menu} from "primereact/menu";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {TabMenu} from "primereact/tabmenu";
import {Toast} from "primereact/toast";
import {formsApi, formsErrorKey} from "./forms-api";
import {useFormsAccess} from "./forms-access";
import {ClearableDropdown, DatatableEmptyMessage, DROPDOWN_PT, Icon, PageHeaderButton, currentWorkspace, useFormat, useT} from "./host";
import {EmptyState, StatTile, StatusTag, useRelative} from "./forms-ui";
import Submissions from "./submissions";

const TABS = [["forms", "mi mi:list_alt"], ["responses", "mi mi:inbox"], ["templates", "mi mi:dashboard_customize"]];
const DESTINATIONS = ["form_responses", "customer", "project"];
const STATUSES = ["draft", "published", "disabled", "archived"];

export function PlanGate({access}) {
    const t = useT();
    if (!access) return <div className="p-3"><Skeleton height="8rem" className="border-round-2xl"/></div>;
    return (
        <div className="h-full w-full flex align-items-center justify-content-center surface-ground p-3">
            <div className="surface-card border-1 border-200 border-round-2xl p-5 md:w-6 lg:w-4">
                <EmptyState icon="pi pi-lock" title={t("forms.plan.title")} description={t("forms.plan.description")}>
                    {access.canManage && <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:workspace_premium" className="text-base"/>} label={t("forms.plan.see-plans")} onClick={() => window.location.assign("#/settings?tab=subscriptions")}/>}
                </EmptyState>
            </div>
        </div>
    );
}

function StatCard({icon, label, value}) {
    const format = useFormat();
    return (
        <div className="col-6 lg:col-3 p-1">
            <div className="surface-card border-1 border-200 border-round-2xl p-3 flex align-items-center gap-3 h-full">
                <span className="surface-100 border-round-xl w-3rem h-3rem flex align-items-center justify-content-center flex-none"><Icon icon={icon} className="text-2xl text-primary"/></span>
                <div className="flex flex-column gap-1 min-w-0">
                    <strong className="text-xl text-900">{value === undefined ? <Skeleton width="2rem" height="1.25rem"/> : format.number(value)}</strong>
                    <span className="text-xs text-500">{label}</span>
                </div>
            </div>
        </div>
    );
}

export function TemplatesGrid({templates, onPick, busyKey}) {
    const t = useT();
    const format = useFormat();
    if (!templates) return <div className="grid m-0">{[1, 2, 3].map((index) => <div key={index} className="col-12 md:col-6 xl:col-4 p-1"><Skeleton height="7rem" className="border-round-2xl"/></div>)}</div>;
    return (
        <div className="grid m-0">
            {templates.map((template) => (
                <div key={template.key} className="col-12 md:col-6 xl:col-4 p-1">
                    <div className="h-full surface-card border-1 border-200 border-round-2xl p-3 flex flex-column align-items-start gap-2">
                        <span className="surface-100 border-round-xl w-2rem h-2rem flex align-items-center justify-content-center">
                            <Icon icon={`mi mi:${TEMPLATE_ICONS[template.key] || "description"}`} className="text-lg text-primary"/>
                        </span>
                        <strong className="text-sm text-800">{t(`forms.template.${template.key}.title`)}</strong>
                        <span className="text-xs text-500 line-height-3 flex-1">{t(`forms.template.${template.key}.description`)}</span>
                        <span className="text-xs text-400">{t(`forms.template.category.${template.category}`)} · {t("forms.count.questions", {n: format.number(template.fields)})}</span>
                        <Button type="button" outlined className="text-xs gap-2 w-full justify-content-center" icon={<Icon icon="mi mi:add" className="text-base"/>} label={t("forms.templates.use")}
                                loading={busyKey === template.key} disabled={Boolean(busyKey) && busyKey !== template.key} onClick={() => onPick(template.key)}/>
                    </div>
                </div>
            ))}
        </div>
    );
}
const TEMPLATE_ICONS = {blank: "note_add", sales_lead: "work", satisfaction: "star", hiring: "person_add", support: "support_agent", internal_request: "move_to_inbox", webinar: "videocam"};

function FormResponsesPage({formKey, canManage, onBack, rootLabel}) {
    const t = useT();
    const format = useFormat();
    const [form, setForm] = useState(null);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        setForm(null);
        setStats(null);
        formsApi.get(formKey).then(setForm).catch((loadError) => setError(formsErrorKey(loadError)));
        formsApi.analytics(formKey).then(setStats).catch(() => setStats({total: 0, failedActions: 0, needsReview: 0}));
    }, [formKey]);
    const processed = stats?.total ? Math.round(((stats.total - stats.failedActions - stats.needsReview) * 100) / stats.total) : null;
    const title = form?.draft?.meta?.title || form?.title || "";
    return (
        <div className="h-full w-full flex flex-column overflow-hidden surface-ground pb-3">
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs"
                        model={[{label: rootLabel, command: onBack}, {label: t("forms.responses.page-title", {title})}]}/>
            <div className="px-3 flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="flex align-items-center gap-2 min-w-0">
                    <Button type="button" text rounded className="p-2" icon={<Icon icon="mi mi:arrow_forward" className="text-lg"/>} aria-label={t("forms.responses.back")} onClick={onBack}/>
                    <div className="flex flex-column gap-1 min-w-0">
                        <h1 className="m-0 text-lg text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{form ? t("forms.responses.page-title", {title}) : <Skeleton width="12rem" height="1.5rem"/>}</h1>
                        <span className="text-xs text-500">{t("forms.responses.page-hint")}</span>
                    </div>
                </div>
                <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:arrow_forward" className="text-base"/>} label={t("forms.responses.back")} onClick={onBack}/>
            </div>
            <div className="p-3 flex flex-column gap-3 flex-1 min-h-0 overflow-auto">
                {error && <Message severity="error" className="w-full justify-content-start text-xs" text={t(error)}/>}
                <div className="grid m-0">
                    <div className="col-12 md:col-4 p-1"><StatTile icon="pi pi-inbox" label={t("forms.responses.total")} value={stats ? format.number(stats.total) : "—"}/></div>
                    <div className="col-12 md:col-4 p-1"><StatTile icon="pi pi-check-circle" tone="text-green-500" label={t("forms.responses.processed")} value={processed === null ? "—" : t("forms.percent", {n: format.number(processed)})}/></div>
                    <div className="col-12 md:col-4 p-1"><StatTile icon="pi pi-directions" label={t("forms.responses.destination")} value={form ? t(`forms.connect.dest.${form.destination || "form_responses"}`) : "—"}/></div>
                </div>
                <Submissions formKey={formKey} forms={[{key: formKey, title}]} canManage={canManage}/>
                <Message severity="info" className="w-full justify-content-start text-xs" text={t("forms.responses.page-note")}/>
            </div>
        </div>
    );
}

export default function FormsHome(props) {
    const t = useT();
    const format = useFormat();
    const relative = useRelative(format);
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const access = useFormsAccess(currentWorkspace());
    const toast = useRef(null);
    const menu = useRef(null);
    const [menuForm, setMenuForm] = useState(null);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState({q: "", status: null, destination: null});
    const [options, setOptions] = useState(null);
    const [busy, setBusy] = useState("");
    const tab = TABS.some(([key]) => key === params.get("tab")) ? params.get("tab") : "forms";

    const latest = useRef(0);
    const load = useCallback(() => {
        const request = ++latest.current;
        setError("");
        formsApi.list({q: filters.q || undefined, status: filters.status || undefined, destination: filters.destination || undefined})
            .then((next) => { if (request === latest.current) setData(next); })
            .catch((loadError) => { if (request === latest.current) setError(formsErrorKey(loadError)); });
    }, [filters]);

    useEffect(() => {
        if (!access?.allowed) return undefined;
        const timer = setTimeout(load, filters.q ? 300 : 0);
        return () => clearTimeout(timer);
    }, [access, load, filters.q]);

    useEffect(() => {
        if (access?.allowed && !options) formsApi.options().then(setOptions).catch(() => setOptions({templates: []}));
    }, [access, options]);

    if (!access?.allowed) return <PlanGate access={access}/>;
    if (params.get("responses")) {
        return <FormResponsesPage formKey={params.get("responses")} canManage={access.canManage} rootLabel={t(props.name || "forms.title")} onBack={() => setParams({})}/>;
    }

    const toastError = (failure) => toast.current?.show({severity: "error", summary: t(formsErrorKey(failure)), className: "rtl text-xs"});
    const create = async (input, key) => {
        setBusy(key || "new");
        try {
            const form = await formsApi.create(input);
            navigate(`/forms/${form.key}`);
        } catch (createError) {
            toastError(createError);
        } finally {
            setBusy("");
        }
    };
    const act = async (work, summaryKey) => {
        try {
            await work();
            toast.current?.show({severity: "success", summary: t(summaryKey), life: 2500, className: "rtl text-xs"});
            load();
        } catch (actError) {
            toastError(actError);
        }
    };
    const copyLink = async (form) => {
        try {
            const full = await formsApi.get(form.key);
            if (!full.link?.url) throw new Error("no-link");
            await navigator.clipboard.writeText(full.link.url);
            toast.current?.show({severity: "success", summary: t("forms.toast.link-copied"), life: 2000, className: "rtl text-xs"});
        } catch {
            toast.current?.show({severity: "warn", summary: t("forms.toast.not-published"), className: "rtl text-xs"});
        }
    };

    const menuItems = menuForm ? [
        {label: t("forms.action.edit"), icon: "pi pi-pencil", command: () => navigate(`/forms/${menuForm.key}`)},
        {label: t("forms.action.responses"), icon: "pi pi-inbox", command: () => setParams({responses: menuForm.key})},
        ...(menuForm.version ? [{label: t("forms.action.copy-link"), icon: "pi pi-link", command: () => copyLink(menuForm)}] : []),
        ...(access.canManage ? [
            {label: t("forms.action.duplicate"), icon: "pi pi-copy", command: () => create({duplicateOf: menuForm.key, title: t("forms.copy-of", {title: menuForm.title})}, menuForm.key)},
            menuForm.status === "published"
                ? {label: t("forms.action.disable"), icon: "pi pi-pause", command: () => act(() => formsApi.status(menuForm.key, "disable"), "forms.toast.disabled")}
                : menuForm.status === "disabled" ? {label: t("forms.action.enable"), icon: "pi pi-play", command: () => act(() => formsApi.status(menuForm.key, "enable"), "forms.toast.enabled")} : null,
            menuForm.status === "archived"
                ? {label: t("forms.action.unarchive"), icon: "pi pi-replay", command: () => act(() => formsApi.status(menuForm.key, "unarchive"), "forms.toast.unarchived")}
                : {label: t("forms.action.archive"), icon: "pi pi-box", command: () => act(() => formsApi.status(menuForm.key, "archive"), "forms.toast.archived")},
        ].filter(Boolean) : []),
    ] : [];

    const stats = data?.stats || {};
    const hasFilters = Boolean(filters.q || filters.status || filters.destination);
    return (
        <div className="h-full w-full flex flex-column overflow-hidden surface-ground pb-3">
            <Toast ref={toast} position="bottom-left"/>
            <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={[{label: t(props.name || "forms.title")}]}/>
            <PageHeaderButton title={props.name || "forms.title"} description="forms.description" end={(
                <div className="flex flex-wrap align-items-center gap-2">
                    {access.trial && <span className="inline-flex align-items-center gap-2 border-1 border-primary border-round-3xl px-2 py-1 text-xs text-primary surface-card"><i className="pi pi-circle-fill text-2xs"/>{t("forms.trial-badge")}</span>}
                    <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:dashboard_customize" className="text-lg ml-1"/>} label={t("forms.tab.templates")} onClick={() => setParams({tab: "templates"})}/>
                    {access.canManage && <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:add" className="text-lg ml-1"/>} label={t("forms.new")} loading={busy === "new"} onClick={() => create({title: t("forms.untitled")})}/>}
                </div>
            )}/>
            <div className="p-3 flex flex-column gap-3 flex-1 min-h-0 overflow-auto">
                <div className="grid m-0">
                    <StatCard icon="mi mi:description" label={t("forms.stats.total")} value={data ? stats.total : undefined}/>
                    <StatCard icon="mi mi:send" label={t("forms.stats.published")} value={data ? stats.published : undefined}/>
                    <StatCard icon="mi mi:inbox" label={t("forms.stats.month")} value={data ? stats.monthResponses : undefined}/>
                    <StatCard icon="mi mi:error" label={t("forms.stats.review")} value={data ? stats.review : undefined}/>
                </div>
                <TabMenu className="text-xs" model={TABS.map(([key, icon]) => ({label: t(`forms.tab.${key}`), icon: <Icon icon={icon} className="text-base ml-2"/>}))}
                         activeIndex={TABS.findIndex(([key]) => key === tab)} onTabChange={(event) => setParams({tab: TABS[event.index][0]})}
                         pt={{root: () => ({className: "bg-transparent"}), menu: () => ({className: "bg-transparent"})}}/>
                {tab === "forms" && (
                    <>
                        <section role="search" aria-label={t("forms.filters")} className="flex flex-column md:flex-row md:align-items-center gap-2">
                            <IconField className="align-items-center flex flex-1 min-w-0">
                                <InputIcon className="pi pi-search"/>
                                <InputText className="w-full h-3rem border-round-lg pt-2 pb-2 text-sm" value={filters.q} placeholder={t("forms.search")} onChange={(event) => setFilters((current) => ({...current, q: event.target.value}))}/>
                            </IconField>
                            <div className="flex gap-2 md:w-6 lg:w-4">
                                <ClearableDropdown value={filters.status} placeholder={t("forms.filter.status")} className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                                   options={STATUSES.map((value) => ({value, label: t(`forms.status.${value}`)}))} clearAriaLabel={t("forms.filter.clear")}
                                                   onChange={(event) => setFilters((current) => ({...current, status: event.value}))}/>
                                <ClearableDropdown value={filters.destination} placeholder={t("forms.filter.destination")} className="text-xs h-3rem border-round-lg align-items-center" panelClassName="rtl" pt={DROPDOWN_PT}
                                                   options={DESTINATIONS.map((value) => ({value, label: t(`forms.destination.${value}`)}))} clearAriaLabel={t("forms.filter.clear")}
                                                   onChange={(event) => setFilters((current) => ({...current, destination: event.value}))}/>
                            </div>
                        </section>
                        {error ? (
                            <div className="surface-card border-1 border-200 border-round-2xl flex flex-column align-items-center gap-3 p-5">
                                <Message severity="error" text={t(error)}/>
                                <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:refresh" className="text-base"/>} label={t("retry")} onClick={load}/>
                            </div>
                        ) : data && data.forms.length === 0 && !hasFilters ? (
                            <div className="surface-card border-1 border-200 border-round-2xl p-3">
                                <EmptyState icon="pi pi-file-edit" title={t("forms.empty.title")} description={t("forms.empty.description")}/>
                                {access.canManage && <TemplatesGrid templates={options?.templates} busyKey={busy} onPick={(key) => create({templateKey: key}, key)}/>}
                            </div>
                        ) : (
                            <DataTable value={data?.forms || []} loading={!data} emptyMessage={<DatatableEmptyMessage loading={!data}/>} size="small" rowHover selectionMode="single"
                                       className="text-xs surface-card border-1 border-200 border-round-2xl overflow-hidden" onRowClick={(event) => navigate(`/forms/${event.data.key}`)}>
                                <Column className="text-right" header={t("forms.column.form")} body={(form) => (
                                    <div className="flex flex-column gap-1">
                                        <strong className="text-xs text-800">{form.title}</strong>
                                        <span className="text-xs text-400">{t("forms.count.questions", {n: format.number(form.questions)})}{form.version ? ` · ${t("forms.version", {n: format.number(form.version)})}` : ""}{form.owner?.name ? ` · ${form.owner.name}` : ""}</span>
                                    </div>
                                )}/>
                                <Column className="text-right" header={t("forms.column.status")} body={(form) => <StatusTag status={form.status}/>}/>
                                <Column className="text-right" header={t("forms.column.destination")} body={(form) => t(`forms.destination.${form.destination || "form_responses"}`)}/>
                                <Column className="text-right" header={t("forms.column.responses")} body={(form) => format.number(form.submissions)}/>
                                <Column className="text-right" header={t("forms.column.last-response")} body={(form) => relative(form.lastSubmissionAt)}/>
                                <Column className="text-right" header={t("forms.column.updated")} body={(form) => relative(form.updatedAt)}/>
                                <Column className="text-left" body={(form) => (
                                    <div className="flex justify-content-end gap-1">
                                        <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:edit" className="text-lg"/>} aria-label={t("forms.action.edit")} tooltip={t("forms.action.edit")} tooltipOptions={{position: "top"}}
                                                onClick={(event) => { event.stopPropagation(); navigate(`/forms/${form.key}`); }}/>
                                        <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:link" className="text-lg"/>} aria-label={t("forms.action.copy-link")} tooltip={t("forms.action.copy-link")} tooltipOptions={{position: "top"}}
                                                disabled={!form.version} onClick={(event) => { event.stopPropagation(); copyLink(form); }}/>
                                        <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:inbox" className="text-lg"/>} aria-label={t("forms.action.responses")} tooltip={t("forms.action.responses")} tooltipOptions={{position: "top"}}
                                                onClick={(event) => { event.stopPropagation(); setParams({responses: form.key}); }}/>
                                        <Button type="button" text rounded className="p-1" icon={<Icon icon="mi mi:more_horiz" className="text-lg"/>} aria-label={t("forms.action.more")}
                                                onClick={(event) => { event.stopPropagation(); setMenuForm(form); menu.current?.toggle(event); }}/>
                                    </div>
                                )}/>
                            </DataTable>
                        )}
                        <Menu model={menuItems} popup ref={menu} className="rtl text-xs"/>
                    </>
                )}
                {tab === "responses" && <Submissions forms={data?.forms || []} canManage={access.canManage}/>}
                {tab === "templates" && (
                    <div className="surface-card border-1 border-200 border-round-2xl p-3 flex flex-column gap-2">
                        <div className="flex flex-column gap-1 px-1">
                            <strong className="text-sm text-800">{t("forms.templates.title")}</strong>
                            <span className="text-xs text-500">{t("forms.templates.description")}</span>
                        </div>
                        {access.canManage
                            ? <TemplatesGrid templates={options?.templates} busyKey={busy} onPick={(key) => create({templateKey: key}, key)}/>
                            : <EmptyState icon="pi pi-lock" description={t("forms.templates.manage-only")}/>}
                    </div>
                )}
            </div>
        </div>
    );
}
